import { isLiveBooking, studioDayKey } from '@atink/domain';
import { resolveDayOff } from '@/domain/artistAdmin';
import type { Artist, Booking, SessionLength } from '@/domain/types';

/** Minutes that make a calendar day a "full ~8h day" for an artist. */
export const FULL_DAY_MINUTES = 480;
/** Safety bound on the backward walk so it always terminates. */
const LOOKBACK_DAYS = 90;

/** Statuses that represent real (scheduled or worked) chair time. */
const COUNTED_STATUSES: ReadonlySet<Booking['status']> = new Set([
  'confirmed', 'checked-in', 'in-chair', 'completed',
]);

export function isCountableBooking(b: Booking): boolean {
  return COUNTED_STATUSES.has(b.status);
}

/**
 * Did the artist work a full ~8h day on this calendar day? True if a `full-day`
 * sitting is booked, or the counted sittings total ≥ 8h. A short day (e.g. 3h)
 * is not a full day.
 */
export function isFullDayWorked(bookingsOnDay: Booking[]): boolean {
  const counted = bookingsOnDay.filter(isCountableBooking);
  if (counted.some((b) => b.sessionLength === 'full-day')) return true;
  const totalMin = counted.reduce((sum, b) => sum + b.durationMin, 0);
  return totalMin >= FULL_DAY_MINUTES;
}

/**
 * Is this single booking a full-day session? A `full-day` sitting, or a session
 * of >= 8h. Unlike `isFullDayWorked` (which totals a whole day's chair time),
 * this asks about ONE booking - the unit a double day is counted in.
 * // ASSUMPTION: a "full-day session" = sessionLength 'full-day' OR durationMin >= FULL_DAY_MINUTES (480). The nominal 11-6 block is 7h; tag such sittings 'full-day' to count. Confirm the threshold with the client.
 */
export function isFullDaySession(b: Booking): boolean {
  if (!isCountableBooking(b)) return false;
  return b.sessionLength === 'full-day' || b.durationMin >= FULL_DAY_MINUTES;
}

/**
 * A "double day" (locked 14 Jul definition): a single calendar day on which the
 * artist works TWO full-day sessions - the nominal day block (11-6) plus an
 * evening block (6pm onward). Full days on DIFFERENT calendar days are NOT a
 * double day; the earlier "run of N consecutive full days = N-1 doubles" rule
 * is rejected.
 */
export function isDoubleDay(bookingsOnDay: Booking[]): boolean {
  return bookingsOnDay.filter(isFullDaySession).length >= 2;
}

/**
 * Studio day key (Melbourne) of a booking start, or of a week-day entry that may
 * be a plain `yyyy-MM-dd` (kept as is) or an instant. Host-local `format()` put an
 * early-morning session on the previous day in a non-Melbourne browser (88v6 /
 * 7fra class of bug).
 */
function dayKeyOf(isoOrKey: string): string {
  return /^\d{4}-\d{2}-\d{2}$/.test(isoOrKey) ? isoOrKey : studioDayKey(isoOrKey);
}

/** The studio day before `key` (pure calendar arithmetic, no host zone, no DST gap). */
function previousDayKey(key: string): string {
  const [y, m, d] = key.split('-').map(Number);
  return new Date(Date.UTC(y!, m! - 1, d! - 1)).toISOString().slice(0, 10);
}

/**
 * A host-local Date on the studio calendar day `key` (noon, clear of any DST
 * edge), for `resolveDayOff`, which reads a Date's local calendar day.
 */
function calendarDate(key: string): Date {
  const [y, m, d] = key.split('-').map(Number);
  return new Date(y!, m! - 1, d!, 12);
}

function groupByDay(artistId: string, bookings: Booking[]): Map<string, Booking[]> {
  const byDay = new Map<string, Booking[]>();
  for (const b of bookings) {
    if (b.artistId !== artistId) continue;
    const key = studioDayKey(b.start);
    const list = byDay.get(key);
    if (list) list.push(b);
    else byDay.set(key, [b]);
  }
  return byDay;
}

/** Statuses that mean a sitting is being or has been worked (in the chair or done). */
const WORKED_STATUSES: ReadonlySet<Booking['status']> = new Set(['in-chair', 'completed']);

/** Studio days (yyyy-MM-dd) a comp block already compensates. */
function compensatedDays(artist: Artist): Set<string> {
  return new Set(
    artist.availability.leaveBlocks
      .filter((b) => b.type === 'comp' && b.compensatesDate)
      .map((b) => b.compensatesDate!),
  );
}

/**
 * Total double days since the artist's last day off. Walks back from `asOfIso`,
 * counting each calendar day that is a double day (two full-day sessions on that
 * day), and stops at the first rostered day off (weekly rest, RDO, comp, leave —
 * see `resolveDayOff`), which bounds "since last day off".
 *
 * artists-2: today counts only once its double is being worked (both sittings in
 * the chair or done), so a merely-booked evening session doesn't light the badge
 * at 9am. A double a future comp day already compensates still counts: the badge
 * reads literally ("since last day off") until that comp day is taken (75n7).
 */
export function countDoubleDaysSinceLastDayOff(artist: Artist, bookings: Booking[], asOfIso: string): number {
  const byDay = groupByDay(artist.id, bookings);
  const today = studioDayKey(asOfIso);
  let doubleDays = 0;
  let key = today;
  for (let i = 0; i < LOOKBACK_DAYS; i += 1) {
    if (resolveDayOff(artist.availability, calendarDate(key)) !== null) return doubleDays;
    const day = byDay.get(key) ?? [];
    const counted = key === today ? day.filter((b) => WORKED_STATUSES.has(b.status)) : day;
    if (isDoubleDay(counted)) doubleDays += 1;
    key = previousDayKey(key);
  }
  return doubleDays;
}

/**
 * The calendar date (yyyy-MM-dd) of the most recent double day not already
 * referenced by a comp block's `compensatesDate`. Used to pre-fill the comp-day
 * note. Returns null if there is none since the last day off.
 */
export function mostRecentUncompensatedDoubleDay(artist: Artist, bookings: Booking[], asOfIso: string): string | null {
  const compensated = compensatedDays(artist);
  const byDay = groupByDay(artist.id, bookings);
  let key = studioDayKey(asOfIso);
  for (let i = 0; i < LOOKBACK_DAYS; i += 1) {
    if (resolveDayOff(artist.availability, calendarDate(key)) !== null) break;
    if (isDoubleDay(byDay.get(key) ?? []) && !compensated.has(key)) return key;
    key = previousDayKey(key);
  }
  return null;
}

/** The weekly session budget (full-day session units). Display/warning only. */
// ASSUMPTION: budget constant = 5, client-adjustable later.
export const WEEKLY_SESSION_BUDGET = 5;

/**
 * Full-day session units the artist has this week across `weekDays` (each a
 * yyyy-MM-dd or ISO date). A double day contributes 2 because it has two
 * full-day sessions. Display/warning only - comp days remain manual.
 */
export function sessionsThisWeek(artist: Artist, bookings: Booking[], weekDays: readonly string[]): number {
  const days = new Set(weekDays.map(dayKeyOf));
  const byDay = groupByDay(artist.id, bookings);
  let units = 0;
  for (const [key, dayBookings] of byDay) {
    if (!days.has(key)) continue;
    units += dayBookings.filter(isFullDaySession).length;
  }
  return units;
}

export type DoubleDaySeverity = 'none' | 'warning' | 'critical';

export function doubleDaySeverity(count: number): DoubleDaySeverity {
  if (count >= 2) return 'critical';
  if (count === 1) return 'warning';
  return 'none';
}

/** A booking being proposed (New Booking, the Agent's pinned slot). */
export interface ProposedSession {
  start: string;
  durationMin: number;
  sessionLength: SessionLength;
  /** The booking being edited, so it never counts against itself. */
  excludeBookingId?: string;
}

export interface DoubleDayWarning {
  /** Studio day (yyyy-MM-dd) the double falls on. */
  day: string;
  /** Full sessions the artist would have that day, counting the proposed one. */
  fullSessions: number;
}

/**
 * The booking-time double-day warning (B9 / 14ym9bv75nk, Anas 1 Oct Q2): a
 * double day is a SECOND FULL SESSION ON THE SAME DAY — an artist working two
 * days' worth of hours in one day. Warn only, never block; no "past 6pm" clause.
 *
 * Unlike the worked-day counter (committed sittings only), the warning counts
 * every live booking already on the artist's day, held or unpaid included: a
 * booker planning a second full session should hear about it before either one
 * is paid. Days are Melbourne studio days.
 */
export function doubleDayWarningFor(
  artistId: string,
  bookings: Record<string, Booking> | Booking[],
  proposed: ProposedSession,
): DoubleDayWarning | null {
  const isFull = (b: { sessionLength: SessionLength; durationMin: number }) =>
    b.sessionLength === 'full-day' || b.durationMin >= FULL_DAY_MINUTES;
  if (!isFull(proposed)) return null;
  const day = studioDayKey(proposed.start);
  const list = Array.isArray(bookings) ? bookings : Object.values(bookings);
  const existing = list.filter(
    (b) =>
      b.artistId === artistId &&
      b.id !== proposed.excludeBookingId &&
      isLiveBooking(b) &&
      studioDayKey(b.start) === day &&
      isFull(b),
  ).length;
  return existing >= 1 ? { day, fullSessions: existing + 1 } : null;
}
